import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@xstore/shared/api-client';

export interface Bot {
  id: string;
  name: string;
  mode: 'ONLINE' | 'OFFLINE' | 'MAINTENANCE' | 'HOLIDAY';
  telegramUsername?: string;
  workspace?: string;
  environment?: 'production' | 'development' | 'staging';
  webhookUrl: string | null;
  offlineMessage: string | null;
  maintenanceMessage: string | null;
  holidayMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BotContextValue {
  bots: Bot[];
  selectedBot: Bot | null;
  selectBot: (bot: Bot | null) => void;
  isLoading: boolean;
}

const BotContext = createContext<BotContextValue | undefined>(undefined);

const BOT_STORAGE_KEY = 'xstore-selected-bot-id';

export function BotProvider({ children }: { children: ReactNode }) {
  const { data: bots = [], isLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: () => apiClient.get<Bot[]>('/bots'),
  });

  const [selectedBot, setSelectedBot] = useState<Bot | null>(null);

  useEffect(() => {
    if (bots.length === 0) {
      setSelectedBot(null);
      localStorage.removeItem(BOT_STORAGE_KEY);
      return;
    }

    const storedId = localStorage.getItem(BOT_STORAGE_KEY);
    const found = bots.find((b) => b.id === storedId);
    if (found) {
      setSelectedBot(found);
    } else {
      setSelectedBot(bots[0]);
      localStorage.setItem(BOT_STORAGE_KEY, bots[0].id);
    }
  }, [bots]);

  const selectBot = useCallback((bot: Bot | null) => {
    setSelectedBot(bot);
    if (bot) {
      localStorage.setItem(BOT_STORAGE_KEY, bot.id);
    } else {
      localStorage.removeItem(BOT_STORAGE_KEY);
    }
  }, []);

  return (
    <BotContext.Provider value={{ bots, selectedBot, selectBot, isLoading }}>
      {children}
    </BotContext.Provider>
  );
}

export function useBots() {
  const ctx = useContext(BotContext);
  if (ctx == null) throw new Error('useBots must be used within BotProvider');
  return ctx;
}
